import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Box,
  CircularProgress,
  DialogContent,
  Divider,
} from '@mui/material';

import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { getVisibleFieldsOfProcess } from 'store/visibleFields';
import { ButtonContained, ButtonText } from 'components/general';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import {
  useCancelConfirmation,
  useFormCleanupOnUnmount,
} from 'presentation/dialogForms/dialogFormsHooks';
import { showChannelRequestErrorToast, showErrorToastAndLog } from 'services/toaster';
import { useProject } from 'store/project';
import { issueClient } from 'shared/domain/issue/issueClient';
import { useProcesses } from 'store/process';

import { AfterDoneResult } from './afterDoneResult';
import { IssueForm } from './form';
import { addDetectionDate, createInitialValues } from './model';
import {
  FilledIssue,
  IssueWizardProps,
  SetableIssueFields,
} from './types';
import { handleFieldValidation, handleFormValidation } from './validation';
import {
  formDialogBodySx,
  formDialogFooterSx,
} from 'components/general/formDialogLayout/styles';

const SUBMIT_EVENT_TYPE = 'submit-issue';

export const IssueWizard = forwardRef<
  { onCancel: () => Promise<void> },
  IssueWizardProps
>((props, ref) => {
  const formName = 'issue';
  const {
    disabledFields,
    beforeSave,
    afterDone,
    initialValues,
    availableSites,
    availableLevelsPerSite,
    processId,
    processCode,
  } = props;
  const { timezone, _id: projectId } = useProject();
  const { processes, loading: processesLoading } = useProcesses();

  const [isPosting, setIsPosting] = useState<boolean>(false);

  useFormCleanupOnUnmount(formName);

  const submitForm = useCallback(
    async (values: FilledIssue) => {
      setIsPosting(true);
      let additionalValues: { inspection: string | undefined; protocolItem: string | undefined };
      try {
        additionalValues = beforeSave
          ? await beforeSave()
          : { inspection: undefined, protocolItem: undefined };
      } catch (error) {
        setIsPosting(false);
        showErrorToastAndLog('Error while saving inspection before issue creation',
          error
        );
        return;
      }
      const { site, level, process, title } = values;

      try {
        const issue = await issueClient.create({
          primaryData: {
            site: site._id,
            level: level._id,
            title,
            detectionDate: addDetectionDate(
              getVisibleFieldsOfProcess(process._id),
              timezone
            )?.toString(),
          },
          process: process._id,
          projectId,
          ...additionalValues,
        });
        afterDone(issue, AfterDoneResult.saved);
      } catch (error) {
        showChannelRequestErrorToast('Error while creating issue', error);
        afterDone(undefined, AfterDoneResult.cancelled);
      } finally {
        setIsPosting(false);
      }
    },
    [
      afterDone,
      beforeSave,
      timezone,
      projectId,
    ]
  );

  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const formInitialValues: SetableIssueFields = createInitialValues(
    initialValues,
    processId,
    processCode,
    processes
  );

  const needsInspectionProcess = Boolean(
    initialValues && (processId || processCode)
  );
  const inspectionProcessReady =
    !needsInspectionProcess ||
    Boolean(formInitialValues.process) ||
    (!processesLoading && processes.length > 0);

  const confirmCancel = useCancelConfirmation(formName);

  const onCancel = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }

    afterDone(undefined, AfterDoneResult.cancelled);
  }, [afterDone, confirmCancel]);

  useImperativeHandle(
    ref,
    () => ({
      onCancel,
    }),
    [onCancel]
  );

  if (!inspectionProcessReady) {
    return null;
  }

  return (
    <WithInputForm
      values={formInitialValues}
      errors={{}}
      handleFormValidation={handleFormValidation}
      handleFieldValidation={handleFieldValidation}
      onSubmit={submitForm}
      eventType={SUBMIT_EVENT_TYPE}
      dispatchSubmit={releaseSubmitEvent}
      formName={formName}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={formDialogBodySx}>
          <IssueForm 
            disabledFields={disabledFields} 
            availableSites={availableSites}
            availableLevelsPerSite={availableLevelsPerSite}
          />
        </Box>
        <Divider />
        <Box sx={formDialogFooterSx}>
          <ButtonText onClick={onCancel} disabled={isPosting}>
            <FormattedMessage id={CommonTranslationKeys.cancel} />
          </ButtonText>
          <ButtonContained
            onClick={releaseSubmitEvent}
            disabled={isPosting}
            endIcon={
              isPosting ? (
                <CircularProgress
                  size={13}
                  thickness={4}
                  disableShrink
                  color='secondary'
                />
              ) : undefined
            }
          >
            <FormattedMessage id={CommonTranslationKeys.bottomNavCreate} />
          </ButtonContained>
        </Box>
      </DialogContent>
    </WithInputForm>
  );
});
